import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, KeyboardArrowDown } from '@mui/icons-material';
import { osirisTokens } from '@/theme/tokens';

interface PaginationProps {
  /** 0-indexed current page. */
  page: number;
  pageSize: number;
  /** Rows currently visible — used to compute `1-N` range. */
  visibleCount: number;
  /** Total rows if known (omit if backend only returns isMoreAvailable). */
  totalCount?: number;
  /** When totalCount is unknown, controls whether next is enabled. */
  hasMore?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

const DEFAULT_SIZES = [10, 12, 25, 50, 100];

export function Pagination({
  page,
  pageSize,
  visibleCount,
  totalCount,
  hasMore,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_SIZES,
  className,
}: PaginationProps) {
  const start = visibleCount === 0 ? 0 : page * pageSize + 1;
  const end = page * pageSize + visibleCount;

  const canPrev = page > 0;
  const canNext = totalCount !== undefined ? end < totalCount : !!hasMore;

  const cellBorder = `1px solid ${osirisTokens.bg.header}`;
  const labelSx = {
    fontFamily: osirisTokens.fonts.body,
    fontSize: 12,
    color: osirisTokens.text.secondary,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Box
      className={`pagination${className ? ' ' + className : ''}`}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        height: 32,
        mx: 2,
        flexShrink: 0,
      }}
    >
      {/* Spacer cell spanning the rest of the table width */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          borderLeft: cellBorder,
          borderRight: cellBorder,
          borderBottom: cellBorder,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          borderRight: cellBorder,
          borderBottom: cellBorder,
        }}
      >
        <Typography sx={labelSx}>Rows per page:</Typography>
        <Select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          variant="standard"
          disableUnderline
          IconComponent={KeyboardArrowDown}
          className="pagination-page-size"
          sx={{
            fontFamily: osirisTokens.fonts.body,
            fontSize: 12,
            color: osirisTokens.text.primary,
            '& .MuiSelect-select': { py: 0, pl: 0.5, pr: '20px !important' },
            '& .MuiSvgIcon-root': {
              color: osirisTokens.icons.tertiary,
              fontSize: 18,
            },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem
              key={size}
              value={size}
              dense
              sx={{ fontFamily: osirisTokens.fonts.body, fontSize: 12 }}
            >
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          borderRight: cellBorder,
          borderBottom: cellBorder,
        }}
      >
        <Typography sx={labelSx} className="pagination-range">
          {totalCount !== undefined
            ? `${start}-${end} of ${totalCount}`
            : visibleCount === 0
              ? '0 results'
              : `${start}-${end}`}
        </Typography>
      </Box>

      <IconButton
        onClick={() => onPageChange(page - 1)}
        disabled={!canPrev}
        className="pagination-prev"
        aria-label="Previous page"
        sx={{
          width: 32,
          height: 32,
          borderRadius: 0,
          borderRight: cellBorder,
          borderBottom: cellBorder,
          color: osirisTokens.icons.secondary,
        }}
      >
        <ChevronLeft sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton
        onClick={() => onPageChange(page + 1)}
        disabled={!canNext}
        className="pagination-next"
        aria-label="Next page"
        sx={{
          width: 32,
          height: 32,
          borderRadius: 0,
          borderRight: cellBorder,
          borderBottom: cellBorder,
          color: osirisTokens.icons.secondary,
        }}
      >
        <ChevronRight sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
}
